import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../../api/products-client';
import { useAuth } from '../../context/auth-provider';
import { useStaysProduct } from '../../utils/app-features';
import { can, PERMISSIONS } from '../../auth/permissions';
import { AppButton } from '../button/app-button';
import { CardMeta, CardTitle, Inline, PropertyCard } from '../pages/stays/properties/properties.styled';

/**
 * SET UP YOUR FRONT DESK (Independent Stays IS6). Shown to the owner after they
 * add Lobbero Desk to a Stays account, until they dismiss it. Only ever a pointer
 * to screens that already exist; the "dismissed" note is this browser's own
 * convenience, so losing it costs one more look at a short list.
 */

const KEY = 'lobbero.front-desk-checklist.dismissed';

const dismissedHere = (): boolean => {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

export const FrontDeskChecklist: React.FC = () => {
  const { permissions } = useAuth();
  // IS8.0: the checklist follows a product change, which exists only with Lobbero Stays.
  const staysOn = useStaysProduct() === true;
  const allowed = can(permissions, PERMISSIONS.BILLING_READ) && staysOn;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!allowed || dismissedHere()) return undefined;
    let live = true;
    getProducts()
      .then(p => live && setShow(p.front_desk_checklist === true && p.products.desk === true))
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [allowed]);

  if (!show) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(KEY, '1');
    } catch {
      // A private window: it is simply hidden for this visit.
    }
    setShow(false);
  };

  return (
    <PropertyCard as="div" data-front-desk-checklist="">
      <div>
        <CardTitle>Set up your front desk</CardTitle>
        <CardMeta>
          <Link to="/dashboard/buildings">Check your buildings and units</Link>
        </CardMeta>
        <CardMeta>
          <Link to="/dashboard/users">Invite your concierges</Link>
        </CardMeta>
        <CardMeta>
          <Link to="/dashboard/residents">Add your residents</Link>
        </CardMeta>
      </div>
      <Inline>
        <AppButton variant="ghost" onClick={dismiss}>
          Dismiss
        </AppButton>
      </Inline>
    </PropertyCard>
  );
};
